import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Clock, ArrowUpRight, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTABand } from "@/components/CTABand";
import { Seo, localBusinessSchema, breadcrumbSchema } from "@/seo/Seo";
import { POSTS, getPost } from "@/data/blogData";
import { getService } from "@/data/servicesData";
import { BUSINESS } from "@/data/servicesData";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!post) return <Navigate to="/tips" replace />;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Tips & Advice", path: "/tips" },
    { name: post.category, path: "/tips" },
  ];
  const service = post.related ? getService(post.related) : null;
  const more = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: BUSINESS.name },
    publisher: { "@type": "Organization", name: BUSINESS.name },
    mainEntityOfPage: `${ORIGIN}/tips/${post.slug}`,
    description: post.metaDescription,
  };

  return (
    <div className="bg-white min-h-screen" data-testid="blog-post">
      <Seo
        title={post.metaTitle}
        description={post.metaDescription}
        path={`/tips/${post.slug}`}
        image={post.image}
        keywords={post.keywords}
        jsonLd={[articleSchema, localBusinessSchema(), breadcrumbSchema(crumbs)]}
      />
      <Navbar />

      <section className="pt-[110px] sm:pt-[130px] pb-10 blueprint">
        <div className="max-w-[860px] mx-auto px-5 sm:px-8">
          <Breadcrumbs items={crumbs} />
          <span className="inline-block mt-5 bg-[#2563EB] text-white px-3 py-1 font-mono-accent text-[10px] tracking-widest uppercase border-2 border-[#0A1A4A]">{post.category}</span>
          <h1 className="font-display font-black text-[#0A1A4A] text-4xl sm:text-5xl tracking-tight leading-[0.98] mt-4">{post.title}</h1>
          <div className="flex items-center gap-4 font-mono-accent text-[11px] tracking-widest uppercase text-[#0A1A4A]/50 mt-5">
            <span className="flex items-center gap-1.5"><Clock size={13} /> {post.read} read</span>
            <span>·</span>
            <span>By {BUSINESS.name}</span>
          </div>
        </div>
      </section>

      <div className="max-w-[860px] mx-auto px-5 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="border-2 border-[#0A1A4A] shadow-hard overflow-hidden">
          <img src={post.image} alt={post.title} className="w-full h-[300px] sm:h-[420px] object-cover" />
        </motion.div>
      </div>

      <article className="max-w-[860px] mx-auto px-5 sm:px-8 py-12">
        {post.sections.map((s, i) => (
          <div key={i} className="mb-8">
            {s.heading && <h2 className="font-display font-black text-[#0A1A4A] text-2xl sm:text-3xl uppercase tracking-tight mb-4">{s.heading}</h2>}
            {s.p && s.p.map((para, j) => (
              <p key={j} className="text-[#0A1A4A]/80 text-lg leading-relaxed mb-4">{para}</p>
            ))}
            {s.list && (
              <ul className="space-y-3 mt-2">
                {s.list.map((li, k) => (
                  <li key={k} className="flex items-start gap-3 text-[#0A1A4A]/80 text-lg border-l-2 border-[#2563EB] pl-4">{li}</li>
                ))}
              </ul>
            )}
          </div>
        ))}

        {service && (
          <Link to={`/services/${service.slug}`} className="group flex items-center justify-between gap-4 bg-[#0A1A4A] text-white p-6 border-2 border-[#0A1A4A] hover:bg-[#2563EB] transition-colors" data-testid="post-related-service">
            <div>
              <div className="font-mono-accent text-[10px] tracking-widest uppercase text-[#EAB308] mb-1">Related Service</div>
              <div className="font-display font-extrabold uppercase text-xl">{service.name} in Swansea</div>
            </div>
            <ArrowRight className="shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      </article>

      <section className="bg-[#F3F4F6] border-y-2 border-[#0A1A4A] py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <h2 className="font-display font-black text-[#0A1A4A] text-3xl uppercase tracking-tight mb-8">More Tips</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {more.map((m) => (
              <Link key={m.slug} to={`/tips/${m.slug}`} className="group bg-white border-2 border-[#0A1A4A] p-6 hover:bg-[#2563EB] transition-colors" data-testid={`more-post-${m.slug}`}>
                <div className="flex items-start justify-between">
                  <h3 className="font-display font-extrabold uppercase text-base text-[#0A1A4A] group-hover:text-white transition-colors leading-tight">{m.title}</h3>
                  <ArrowUpRight size={18} className="text-[#2563EB] group-hover:text-white transition-colors shrink-0" />
                </div>
                <p className="text-sm text-[#0A1A4A]/60 group-hover:text-white/80 transition-colors mt-2">{m.excerpt.slice(0, 80)}…</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
      <Footer />
    </div>
  );
}
